import * as Yup from 'yup';
import { phone } from 'phone';

const validatePhone = (phoneNumber) => {
  if (!phoneNumber) {
    return true;
  }

  if (phoneNumber === '(555) 555-5555') {
    return true;
  }

  const { isValid } = phone(phoneNumber, { country: 'USA' });
  return isValid;
};

const validateFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
  const maxSize = 1024 * 1024 * 10; // 10MB

  if (file instanceof File) {
    // Validate a File object
    return allowedTypes.includes(file.type) && file.size <= maxSize;
  }

  if (typeof file === 'object' && file !== null) {
    // Validate an object (e.g., one coming from the backend)
    return 'url' in file && file.type === 'image';
  }

  // If neither, it's invalid
  return false;
};

export default function createValidationSchema(fields) {
  const flatFields = Object.keys(fields).reduce((acc, key) => {
    const fieldsSection = fields[key].fields;

    // If the field is an array, we don't want to include it in the validation schema right now
    // Later, we'll handle it separately
    if (fields[key].type === 'array') {
      return acc;
    }

    return [...acc, ...fieldsSection];
  }, []);

  const flatFieldsWithoutComponents = flatFields.filter(
    (field) => field.type !== 'component'
  );

  const validationRules = {};
  const types = {
    editableText: Yup.string(),
    numberPercentageArray: Yup.array(),
    bankAccountNumber: Yup.string(),
    text: Yup.string(),
    password: Yup.string(),
    select: Yup.string(),
    fetchableSelect: Yup.string(),
    number: Yup.string(),
    date: Yup.date(),
    paymentWaterfall: Yup.array(),
    radio: Yup.string(),
    checkbox: Yup.boolean(),
    textarea: Yup.string(),
    currency: Yup.string(),
    percentage: Yup.number()
      .typeError('Must be a number')
      .min(0, 'Cannot be lower than 0'),
    image: Yup.mixed().test(
      'fileTypeAndSize',
      'File must be a JPEG, PNG, or GIF and smaller than 10MB',
      (value) => {
        // If no value is provided, return true (no validation needed)
        if (!value) {
          return true;
        }

        // Check if the value is an array of files
        if (Array.isArray(value)) {
          return value?.every?.((file) => validateFile(file));
        }

        // Otherwise, validate a single file
        return validateFile(value);
      }
    ),
    docUploader: Yup.mixed(),
  };

  const validations = (type, field) => {
    return {
      email: type.email?.('Invalid email'),
      phone: type.test?.('phone', 'Phone number is not valid', validatePhone),
      zip: type.matches?.(/^[0-9]{5}(?:-[0-9]{4})?$/, 'Zip code is not valid'),
      tin: type.test?.('len', 'TIN must be exactly 9 characters', (val) => {
        if (!val) {
          return true;
        }
        return val?.replace(/_/g, '').replace(/-/g, '').length === 9;
      }),
      subdomain: type
        ?.matches?.(
          /^(?!-)[A-Za-z0-9-]{1,61}[A-Za-z0-9]$/,
          'Subdomain can only contain letters, numbers, and hyphens. It cannot start or end with a hyphen.'
        )
        .min(3, 'Subdomain must be at least 3 characters long.')
        .max(63, 'Subdomain cannot be more than 63 characters long.'),
      domain: type?.matches?.(
        /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/,
        'Domain must be in the correct format and cannot include protocols or ports.'
      ),
      pixelId: type?.max?.(3, 'Pixel ID must be 3 characters or fewer.'),
      object: field?.innerFields
        ? type?.of(
            Yup.object()
              .shape(
                field.innerFields.reduce((acc, innerField) => {
                  const innerType = types[innerField.type];
                  let innerValidation =
                    validations(innerType, innerField)[innerField.validation] ||
                    innerType;

                  if (innerField.customValidation) {
                    innerValidation = innerValidation.test(
                      ...innerField.customValidation
                    );
                  }

                  return {
                    ...acc,
                    [innerField.name]: innerField.required
                      ? innerValidation.required('This field is required')
                      : // This validation is used because using required does not allow to accept empty values for array of object
                        // When there is an object in array each field of it should be required, but empty array should be allowed too
                        // We using nulls as empty values for fields
                        innerValidation
                          .nullable()
                          .test(
                            'not-null',
                            'This field is required',
                            (value) => value !== null
                          ),
                  };
                }, {})
              )
              .nullable()
          )
        : type,
    };
  };

  flatFieldsWithoutComponents.forEach((field) => {
    const type = types[field.type];

    let validation = validations(type, field)[field.validation] || type;

    if (field.customValidation) {
      validation = validation.test(...field.customValidation);
    }

    validationRules[field.name] = field.required
      ? validation.required('This field is required')
      : validation;

    if (field.nullable) {
      validationRules[field.name] = validationRules[field.name].nullable();
    }
  });

  const arrayFields = fields.filter((field) => field.type === 'array');

  arrayFields.forEach((arrayField) => {
    const arrayFieldName = arrayField.name;

    const arrayValidation = createValidationSchema([
      {
        title: arrayField.title,
        fields: arrayField.fields,
      },
    ]);

    validationRules[arrayFieldName] = Yup.array().of(arrayValidation);
  });

  return Yup.object(validationRules);
}
